// cw: only Gls can sign up

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import { Link } from 'react-router-dom';
import './style.scss';
import AuthLayout from '../../components/AuthLayout';
import environment from '../../environment';
import { toast } from 'react-toastify';
import FormControl from '../../components/common/FormControl';
import { useSelector } from 'react-redux';

const Signup = () => {
  const history = useNavigate();
  const user = useSelector((state: any) => state.user);

  const [form, setForm]: any = useState({ 
    email: '', 
    password: '', 
    lastName: "", 
    firstName: "", 
    customerRole: environment.glRoleId,
    groupId: null,
  });
  const [remember, setRemember] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [groups, setGroups] = useState([]);
  const [eyes, setEyes] = useState({ password: false });
  const [emailError, setEmailError] = useState('');

  const getGroups = () => {
    ApiClient.get('api/group/list', { status: 'active', sortBy: "" }).then((res: any) => {
      if (res.success) {
        setGroups(res.data);
      }
    });
  };

  const validateEmail = (email: any): boolean => {
    const emailPattern = /^\S+@\S+\.\S+$/;
    return emailPattern.test(email);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validateEmail(form.email)) {
      setEmailError("Email is invalid.");
      return;
    }

    if (!form.groupId) {
      toast.error("Please select a networking group");
      return;
    }

    if (!remember) return;

    let data: any = {
      role: environment.userRoleId,
      customerRole: environment.glRoleId,
      ...form,
      fullName: `${form.firstName} ${form.lastName}`.trim()
    };

    loader(true);

    ApiClient.post('api/user/register', data).then(async res => {
      if (res.success) {
        toast.success("Registration successful! Please verify your email.");
        history('/login');
      }
      loader(false);
    });
  };

  useEffect(() => {
    if (user && user?.loggedIn) {
      history('/dashboard');
    }
    getGroups();
  }, []);

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} autoComplete='off'>
        <h4 className="text-typo mb-2 text-2xl font-bold">Group Leader Registration</h4>
        <p className="text-gray-500 text-sm mb-6">
          Register as a Group Leader to create and manage your networking group.
          <br />
          <span className="text-orange-500">Members are invited by Group Leaders.</span>
        </p>

        <input 
          type="email"
          onChange={(e) => {
            setForm({ ...form, email: e.target.value });
            if (emailError) setEmailError('');
          }}
          value={form.email}
          className="shadow-box border-1 border-gray-300 relative bg-gray-100 mb-3 w-full text-sm placeholder:text-gray-500 rounded-lg h-12 flex items-center gap-2 overflow-hidden px-2 hover:ring-orange-500 focus:border-orange-500"
          placeholder="Email address"
          required 
        />
        {emailError && <p className="text-red-500 text-xs mb-2">{emailError}</p>}

        <input 
          type="text"
          onChange={e => setForm({ ...form, firstName: e.target.value })}
          value={form.firstName}
          className="shadow-box border-1 border-gray-300 relative bg-gray-100 mb-3 w-full text-sm placeholder:text-gray-500 rounded-lg h-12 flex items-center gap-2 overflow-hidden px-2 hover:ring-orange-500 focus:border-orange-500"
          placeholder="First Name"
          required 
        />

        <input 
          type="text"
          onChange={e => setForm({ ...form, lastName: e.target.value })}
          value={form.lastName}
          className="shadow-box border-1 border-gray-300 relative bg-gray-100 mb-3 w-full text-sm placeholder:text-gray-500 rounded-lg h-12 flex items-center gap-2 overflow-hidden px-2 hover:ring-orange-500 focus:border-orange-500"
          placeholder="Last Name"
          required 
        />

        <div className="relative mb-3">
          <input
            type={eyes.password ? 'text' : 'password'}
            className="shadow-box border-1 border-gray-300 relative bg-gray-100 w-full text-sm placeholder:text-gray-500 rounded-lg h-12 flex items-center gap-2 overflow-hidden px-2 hover:ring-orange-500 focus:border-orange-500" 
            placeholder="Password (min 8 characters)"
            onChange={e => setForm({ ...form, password: e.target.value })}
            value={form.password}
            minLength={8}
            required 
          />
          <div className='absolute right-2 inset-y-0 flex items-center text-gray-500 text-sm cursor-pointer'>
            <i className={eyes.password ? 'fa fa-eye' : 'fa fa-eye-slash'} onClick={() => setEyes({ ...eyes, password: !eyes.password })}></i>
          </div>
        </div>

        <div className='mb-3 relative'>
          <FormControl
            type='select'
            options={groups}
            displayValue="name"
            placeholder="Select Networking Group"
            label="Networking Group"
            value={form.groupId}
            onChange={(e: any) => setForm({ ...form, groupId: e })}
            required
          />
        </div>

        <div className='flex'>
          <label className='flex items-start pointer'>
            <input 
              type="checkbox" 
              checked={remember} 
              onChange={(e) => setRemember(e.target.checked)} 
              className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" 
            /> 
            <span className='text-xs text-gray-600'>
              By clicking Create account, I agree that I have read and accepted the{' '}
              <a href="https://skillsnetpro.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Terms and Conditions</a>.
            </span>
          </label>
        </div>

        {submitted && !remember && (
          <div className='text-red-600 text-sm capitalize mt-3'>
            Please agree to our Terms Of Use And Privacy Policy
          </div>
        )}

        <div className="mt-8">
          <button 
            type="submit" 
            className="px-4 w-full text-sm font-normal text-white h-12 flex items-center justify-center gap-2 !bg-orange-500 rounded-lg shadow-btn hover:opacity-80 transition-all focus:ring-2 ring-[#EDEBFC] disabled:bg-[#D0CAF6] disabled:cursor-not-allowed"
          >
            Register as Group Leader
          </button>
        </div>

        <p className='text-sm mt-3 text-center'>
          Already have an account? <Link to="/login" className='text-orange-500 text-sm'>Sign In</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default Signup;